import { Component } from '../Component';
import { HHDSVideoEvent } from '../../utils/VideoTypes';
import { ArgSpecDictionary } from '../ComponentUtils';
import gsap from 'gsap';
import { PageEvent } from '../../common/page-lifecycle/PageEvent';

const DEBUG_VERBOSE: boolean = false;
export const HHDSVideoDirectTagName: string = 'hhds-video-direct';
const TAG_NAME: string = HHDSVideoDirectTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSVideoDirectAttrNames = {
	src: 'src',
	cover: 'cover',
	controls: 'controls',
	autoplay: 'autoplay',
	loop: 'loop',
	loopPoint: 'loop-point',
	startPoint: 'start-point',
};

const Attrs = HHDSVideoDirectAttrNames;

export class HHDSVideoDirect extends Component {
	private listenerFuncs: { [key: string]: EventListener } = {};
	private _videoElement: HTMLVideoElement | null = null;
	private loopPoint: number = 0;
	private startPoint: number = 0;
	private started: boolean = false;
	private _playbackRequested: boolean = false;
	private _coverElement: HTMLElement | null = null;

	constructor() {
		super();
		this.createEventCallbacks();
	}

	private createEventCallbacks(): void {
		this.listenerFuncs['click'] = () => this.play();
		this.listenerFuncs['loadeddata'] = () => this.onVideoLoaded();
		this.listenerFuncs['timeupdate'] = (event: Event) => this.onVideoTimeUpdate(event);
		this.listenerFuncs['play'] = () => this.onVideoPlay();
		this.listenerFuncs['ended'] = () => this.onVideoEnded();
		this.listenerFuncs['pause'] = () => this.onVideoPaused();
		this.listenerFuncs['visibilitychange'] = () => this.onVisibilityChange();
	}

	protected override init(): void {
		const src = this.vars.get<string>(Attrs.src);
		if (!src) return;

		DEBUG_VERBOSE && logger.log(`${LOG_PREFIX} video src: ${src}`);

		this.loopPoint = this.vars.get<number>(Attrs.loopPoint);
		this.startPoint = this.vars.get<number>(Attrs.startPoint);

		let buttonHtml: string;
		if (this.vars.get<boolean>(Attrs.autoplay)) {
			buttonHtml = '';
		} else {
			//buttonHtml = `<button class="${TAG_NAME}__button">PLAY</button>`;
			let styles: string[] = [];
			styles.push('--button-min-width: 64px;');
			styles.push('--button-overlay-min-height: 60px;');
			styles.push('--button-overlay-min-height-md: 60px;');
			styles.push('--button-overlay-min-height-lg: 60px;');
			styles.push('--button-overlay-min-height-xl: 60px;');
			styles.push('--button-overlay-radius: 50%;');
			let iconHtml = `<hhds-icon type="play" slot="start" style="--icon-color: var(--color-neutral-white);"></hhds-icon>`;
			buttonHtml = `<hhds-button type="overlay" style="${styles.join(' ')}">${iconHtml}</hhds-button>`;
		}

		const html: string = `
    <div class="${TAG_NAME}"">
      <video playsinline></video>
      <div class="${TAG_NAME}__cover">
        ${buttonHtml}
      </div>
    </div>`;

		this.setInnerHtml('', '', html);

		this._coverElement = this.root.querySelector(`.${TAG_NAME}__cover`) as HTMLElement;
		const coverImageUrlString = this.vars.get<string>(Attrs.cover);
		if (coverImageUrlString && coverImageUrlString.length > 0) {
			const image = new Image();
			image.addEventListener('load', () => {
				if (this._coverElement) {
					this._coverElement.style.backgroundImage = 'url(' + coverImageUrlString + ')';
				}
				this.emitEvent(HHDSVideoEvent.coverLoaded);
			});
			image.src = coverImageUrlString;
			//this.coverElement.style.backgroundImage = `url('${coverImageUrlString}')`;
		} else {
			this._coverElement.remove();
		}

		const videoElement = this.root.querySelector('video') as HTMLVideoElement;
		this._videoElement = videoElement;
		if (videoElement) {
			videoElement.src = src;
			videoElement.loop = this.vars.get<boolean>(Attrs.loop);
			videoElement.autoplay = this.vars.get<boolean>(Attrs.autoplay);
			videoElement.playsInline = true;
			videoElement.controls = this.vars.get<boolean>(Attrs.controls);
			videoElement.muted = this.vars.get<boolean>(Attrs.autoplay);
			videoElement.style.display = 'none';

			videoElement.addEventListener('loadedmetadata', () => {
				const videoEl = this.root.querySelector('video') as HTMLVideoElement;
				if (videoEl) videoEl.style.display = 'block';
			});
			this.addListeners();
		}

		this.addEventListener(PageEvent.leave, () => {
			this.pause();
		});

		this.emitEvent(HHDSVideoEvent.componentConnected);
	}

	get videoElement(): HTMLVideoElement {
		return this.root.querySelector('video') as HTMLVideoElement;
	}

	get coverElement(): HTMLElement {
		return this._coverElement as HTMLElement;
	}

	override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `destroy`);
		this.removeListeners();
		this._videoElement?.remove();
		this._coverElement?.remove();
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: Element[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}

	get componentElement(): HTMLElement {
		return this.root.querySelector(`.${TAG_NAME}`) as HTMLElement;
	}

	addListeners(): void {
		const button = this.root.querySelector(`.${TAG_NAME}__button`) as HTMLElement;
		button?.addEventListener('click', this.listenerFuncs['click']);
		document.addEventListener('visibilitychange', this.listenerFuncs['visibilitychange']);

		if (this._videoElement) {
			this._videoElement.addEventListener('loadeddata', this.listenerFuncs['loadeddata']);
			this._videoElement.addEventListener('timeupdate', this.listenerFuncs['timeupdate']);
			this._videoElement.addEventListener('play', this.listenerFuncs['play']);
			this._videoElement.addEventListener('ended', this.listenerFuncs['ended']);
			this._videoElement.addEventListener('pause', this.listenerFuncs['pause']);
		}
	}

	removeListeners(): void {
		const button = this.root.querySelector(`.${TAG_NAME}__button`) as HTMLElement;
		button?.removeEventListener('click', this.listenerFuncs['click']);
		document.removeEventListener('visibilitychange', this.listenerFuncs['visibilitychange']);

		if (this._videoElement) {
			this._videoElement.removeEventListener('loadeddata', this.listenerFuncs['loadeddata']);
			this._videoElement.removeEventListener('timeupdate', this.listenerFuncs['timeupdate']);
			this._videoElement.removeEventListener('play', this.listenerFuncs['play']);
			this._videoElement.removeEventListener('ended', this.listenerFuncs['ended']);
			this._videoElement.removeEventListener('pause', this.listenerFuncs['pause']);
		}
	}

	isPlaying(): Promise<boolean> {
		if (!this._videoElement) return Promise.resolve(false);
		return Promise.resolve(!this._videoElement.paused);
	}

	play() {
		if (this._videoElement) {
			this._playbackRequested = true;
			this._videoElement.play();
		}
	}

	pause() {
		if (this._videoElement) {
			this._playbackRequested = true;
			this._videoElement.pause();
		}
	}

	onVideoLoaded() {
		DEBUG_VERBOSE && logger.log('Video loaded');
		this.emitEvent(HHDSVideoEvent.loaded);
		if (this.vars.get<boolean>(Attrs.autoplay)) {
			this._videoElement?.play();
		}
	}

	onVideoTimeUpdate(_event: Event) {
		if (!this.started) {
			this.started = true;
		}
		if (this._videoElement && this.loopPoint > 0 && this._videoElement.currentTime >= this.loopPoint) {
			this.loopPlayback();
		}
		this.emitEvent(HHDSVideoEvent.timeUpdate);
	}

	onVideoPlay() {
		DEBUG_VERBOSE && logger.log('Video playing');
		gsap.to(this._coverElement, { opacity: 0.0, duration: 0.2, onComplete: () => this.toggleCover(false) });
		this.emitEvent(HHDSVideoEvent.play);
	}

	onVideoEnded() {
		DEBUG_VERBOSE && logger.log('Video ended');
		if (this._videoElement && !this._videoElement.loop) {
			this.loopPlayback();
		} else {
			this.emitEvent(HHDSVideoEvent.ended);
			this.toggleCover(true);
		}
	}

	onVideoPaused() {
		DEBUG_VERBOSE && logger.log('Video paused');
		this.emitEvent(HHDSVideoEvent.pause);
	}

	loopPlayback() {
		DEBUG_VERBOSE && logger.log(`Should loop at ${this.loopPoint} to ${this.startPoint}`);
		if (this._videoElement) {
			this._videoElement.currentTime = this.startPoint;
			this._videoElement?.play();
		}
	}

	toggleCover(visible: boolean) {
		if (this._coverElement) {
			this._coverElement.style.display = visible ? 'flex' : 'none';
		}
	}

	onVisibilityChange() {
		DEBUG_VERBOSE && logger.log(`${LOG_PREFIX} Visibility change`);
		if (document.hidden) {
			this.isPlaying().then(() => {
				this.pause();
			});
		} else {
			if (this._playbackRequested) {
				window.setTimeout(() => {
					this.play();
				}, 500);
			}
		}
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.src]: {
		description: 'The video URL.',
		defaultValue: '',
		type: String,
	},
	[Attrs.cover]: {
		description: 'The URL of the cover image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.controls]: {
		description: 'Whether to show the video controls.',
		defaultValue: false,
		type: Boolean,
	},
	[Attrs.autoplay]: {
		description: 'Whether to autoplay the video.',
		defaultValue: false,
		type: Boolean,
	},
	[Attrs.loop]: {
		description: 'Whether to loop the video.',
		defaultValue: false,
		type: Boolean,
	},
	[Attrs.loopPoint]: {
		description: 'A custom time (instead of the end of the video) at which to loop the video.',
		defaultValue: 0,
		type: Number,
	},
	[Attrs.startPoint]: {
		description: 'The time to which the video should be rewinded after looping.',
		defaultValue: 0,
		type: Number,
	},
};
